/* eslint-disable fp/no-mutation */
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Snackbar,
  SwipeableDrawer,
  TextField,
  Typography,
} from '@mui/material'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchLiveClasses,
  fetchLiveClassTitleScreens,
  selectLiveClassTitleScreenByID,
  selectLiveClassTitleScreensStatusByID,
} from 'src/v3/admin/admin.slice'

import { useFormik, Field, FormikProvider, FieldProps } from 'formik'
import * as yup from 'yup'
import axios, { pluckData } from 'src/utilities/http-client'
import { TitleScreen } from 'src/v3/core/types'
import { ImageIcon, TitleScreenIcon, UploadFileIcon } from 'src/v3/core/icons'
import Subheader from 'src/v3/components/subheader'
import {
  ContentType,
  contentTypes,
  FileData,
} from 'src/v3/utilities/filesystem'
import DangerousHtml from 'src/components/common/dangerous-html'
import Wysiwyg from 'src/components/common/wysiwyg'
import { makeStyles } from '@mui/styles'

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'relative',
  },
  overlayChild: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
    transition: 'backgroundColor 0.3s ease',
    display: 'none',
  },
  overlayHover: {
    '&:hover $overlayChild': {
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'block',
    },
  },
}))

let updateImage = (data: { id: string; imageType: string }) =>
  axios
    .put<string>('/api/v2/admin/live-class/title-screen/image', data)
    .then(pluckData)

export default ({ liveClass }: { liveClass: any }): ReactElement => {
  let styles = useStyles()
  let dispatch = useAppDispatch()

  let titleScreen = useAppSelector(
    selectLiveClassTitleScreenByID(liveClass?.titleScreenID || ''),
  )
  let liveClassTitleScreensStatus = useAppSelector(
    selectLiveClassTitleScreensStatusByID(liveClass?.titleScreenID || ''),
  )

  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>(
    undefined,
  )
  let [existingFile, setExistingFile] = useState<string | undefined>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      content: yup.string().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      name: '',
      content: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!titleScreen?.id) {
        axios
          .post<TitleScreen>('/api/v2/admin/live-class/title-screen', {
            name: values.name,
            content: values.content,
          })
          .then(pluckData)
          .then((data) => {
            axios
              .put('/api/v2/admin/live-class', {
                ...liveClass,
                titleScreenID: data.id,
              })
              .then(() => {
                if (fileToUpload) {
                  updateImage({
                    id: data.id || '',
                    imageType:
                      fileToUpload?.extension === 'jpeg' ? 'jpg' : 'png',
                  }).then((response) =>
                    axios.put(response, fileToUpload?.file, {
                      headers: {
                        'Cache-Control': 'max-age=2592000',
                        'content-type': fileToUpload?.type,
                      },
                    }),
                  )
                }
              })
              .then(() => dispatch(fetchLiveClassTitleScreens(data.id!)))
              .then(() => dispatch(fetchLiveClasses()))
          })
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Title Screen')
            dispatch(fetchLiveClassTitleScreens(titleScreen?.id || ''))
            dispatch(fetchLiveClasses())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/admin/live-class/title-screen', {
            id: titleScreen.id,
            name: values.name,
            content: values.content,
          })
          .then(() => {
            if (fileToUpload) {
              updateImage({
                id: titleScreen?.id || '',
                imageType: fileToUpload?.extension === 'jpeg' ? 'jpg' : 'png',
              }).then((response) =>
                axios.put(response, fileToUpload?.file, {
                  headers: {
                    'Cache-Control': 'max-age=2592000',
                    'content-type': fileToUpload?.type,
                  },
                }),
              )
            }
          })
          .then(() =>
            dispatch(fetchLiveClassTitleScreens(titleScreen?.id || '')),
          )
          .then(() => dispatch(fetchLiveClasses()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Title Screen')
            dispatch(fetchLiveClassTitleScreens(titleScreen?.id || ''))
            dispatch(fetchLiveClasses())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  let handleCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    let file = event.currentTarget.files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage(
          'Live Class Title Screen Image Upload Failed: Read File Failed',
        )
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid File Type ${fileType}`)
        return
      }

      let binary = atob(reader.result?.toString().split(',')[1] as string)
      let array = new Array(binary.length)
        .fill('')
        .map((_, i) => binary.charCodeAt(i))
      let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
    }

    reader.readAsDataURL(file!)
  }

  let onFormOpen = () => {
    form.setFieldValue('name', titleScreen?.name || '')
    form.setFieldValue('content', titleScreen?.content || '')
    setExistingFile(titleScreen?.imageName)
    setFormOpen(true)
  }

  useEffect(() => {
    if (!liveClassTitleScreensStatus && liveClass?.titleScreenID) {
      dispatch(fetchLiveClassTitleScreens(liveClass?.titleScreenID))
    }
  }, [liveClassTitleScreensStatus, liveClass])

  useEffect(() => {
    if (!formOpen) {
      setExistingFile('')
      setFileToUpload(undefined)
      form.resetForm()
    }
  }, [formOpen])

  return (
    <>
      <Box sx={{ mt: '-30px' }}>
        <Subheader title="Title Screen" />
      </Box>
      <Button
        variant="outlined"
        startIcon={<TitleScreenIcon />}
        onClick={onFormOpen}
      >
        {!titleScreen ? 'Create' : 'Edit'} Title Screen
      </Button>
      {titleScreen && (
        <div
          className={`${styles.overlay} ${styles.overlayHover}`}
          style={{
            backgroundImage: `url(${import.meta.env.VITE_CLOUDFRONT_URL}/public/${titleScreen?.imageName})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'black',
              opacity: 0.8,
            }}
          ></div>
          <div style={{ position: 'relative' }}>
            <Box sx={{ mt: 2, maxHeight: '500px', overflow: 'scroll' }}>
              <DangerousHtml
                htmlVal={titleScreen?.content ?? ''}
                isEncoded={true}
              />
            </Box>
          </div>
        </div>
      )}
      <SwipeableDrawer
        anchor="bottom"
        open={formOpen}
        onOpen={onFormOpen}
        onClose={() => setFormOpen(false)}
        style={{ zIndex: 2000 }}
      >
        <Container maxWidth="lg">
          <Card className="m-4 overflow-auto vh-full">
            <FormikProvider value={form}>
              <form className="p-4" onSubmit={form.handleSubmit}>
                <TextField
                  sx={{ mb: 2 }}
                  fullWidth
                  variant="standard"
                  value={form.values.name}
                  label="Name"
                  name="name"
                  onChange={form.handleChange}
                  error={form.touched.name && !!form.errors.name}
                />
                {document && (
                  <Field name="content">
                    {({ field }: FieldProps) => (
                      <Wysiwyg
                        value={form.values.content}
                        onChange={field.onChange(field.name)}
                      />
                    )}
                  </Field>
                )}
                <DangerousHtml htmlVal={form.values.content} isEncoded={true} />
                <Box>
                  <label style={{ marginTop: 0 }}>
                    <Button
                      component="span"
                      variant="outlined"
                      startIcon={<ImageIcon />}
                    >
                      <input
                        accept="image/*"
                        style={{ display: 'none' }}
                        type="file"
                        onChange={(e) => handleCapture(e)}
                      />
                      {!existingFile ? 'Select' : 'Overwrite'} Background Image
                    </Button>
                  </label>
                  {fileToUpload && (
                    <Typography variant="subtitle1">
                      <UploadFileIcon
                        color="primary"
                        sx={{ mr: '2px', mb: '2px' }}
                      />
                      {fileToUpload.name}
                    </Typography>
                  )}
                  {existingFile && (
                    <Typography color="text.secondary" variant="subtitle2">
                      *The existing background will only be overwritten upon
                      submitting this form with a new image uploaded.
                    </Typography>
                  )}
                </Box>
                <div className="flex flex-row-reverse">
                  {saving ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        className="mr-3"
                        type="submit"
                      >
                        Submit
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        className="mr-3"
                        onClick={() => setFormOpen(false)}
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                </div>
              </form>
            </FormikProvider>
          </Card>
        </Container>
      </SwipeableDrawer>
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  )
}
